import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router'; // Ensure links are activated natively
import { Store } from '@ngrx/store';

import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip'; // Added NzTooltipModule
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { ProjectDataActions } from '@rapider/project-data';

import { DevAgentActions } from '@rapider/dev-agent';

import {
  AiAction,
  AIChatMessage,
  AI_CHAT_CONFIG,
  getActionButtonLabels,
  getOperationIconAndColor,
  getHttpMethodColorClass,
  separateRapiderMetadata,
  getExtractedMetaTitlePure,
  normalizePayloadToArray,
  generateTempId,
  isDevAgentRequiredError,
  isServerAction,
} from '../ai-chat.config';

/**
 * @class ChatEndpointActionCardComponent
 * @description A standalone UI component that renders an "Action Card" for 'endpoint' and 'server_action' AI actions.
 * Displays structural JSON data payloads natively, adhering strictly to Angular encapsulation conventions.
 * Supports 3D flipping to show request vs response layouts.
 */
@Component({
  selector: 'rapider-chat-endpoint-action-card',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzPopconfirmModule,
    NzButtonModule,
    NzTagModule,
    NzTooltipModule,
    RapiderJsonViewerComponent,
  ],
  templateUrl: './chat-endpoint-action-card.component.html',
})
export class ChatEndpointActionCardComponent implements OnChanges {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  @Input({ required: true }) action!: AiAction;
  @Input({ required: true }) message!: AIChatMessage;

  @Input() index!: number;

  // NEW VISUAL STATE BINDINGS
  @Input() isDiscarded = false;
  @Input() isCollapsed = false;
  @Input() hideExecuteButton = false;
  @Input() entityName?: string; // Sourced strictly from the parent wrapper

  @Output() execute = new EventEmitter<AiAction>();
  @Output() discard = new EventEmitter<AiAction>();
  @Output() undoDiscard = new EventEmitter<AiAction>();
  @Output() toggleCollapse = new EventEmitter<void>(); // Fired for local body collapsing

  public readonly config = AI_CHAT_CONFIG;
  public getOperationIconAndColor = getOperationIconAndColor;
  public getHttpMethodColorClass = getHttpMethodColorClass;
  public getActionButtonLabels = getActionButtonLabels;
  public normalizePayloadToArray = normalizePayloadToArray;
  public isDevAgentRequiredError = isDevAgentRequiredError;
  public isServerAction = isServerAction;

  /**
   * Determines whether the given AI action triggers a Rapider workflow rather than a plain endpoint call.
   * Used to hide result-oriented UI (e.g. "Show in Canvas") for workflow triggers, whose response is asynchronous.
   * @param {AiAction} action - The AI action rendered by this card.
   * @returns {boolean} True when the action represents a workflow trigger.
   */
  public isWorkflowTrigger(action: AiAction): boolean {
    const op = (action.operation || '').toLowerCase();
    return op.includes('workflow') || action.type === 'rapider-workflow';
  }

  public cleansedData = signal<any>(null);
  public extractedMeta = signal<any>(null);

  // Controls the 3D flip card state
  public isFlipped = signal<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    const httpPayload = this.action?.payload?.http;
    const inputPayload = this.action?.payload?.input;
    const payloadToDisplay =
      httpPayload?.body || httpPayload?.params || inputPayload;
    if (payloadToDisplay) {
      const processed = separateRapiderMetadata(payloadToDisplay);
      this.cleansedData.set(processed.cleansed);
      this.extractedMeta.set(processed.extracted);
    } else {
      this.cleansedData.set(null);
      this.extractedMeta.set(null);
    }
  }

  public getExtractedMetaTitle(index: number): string | null {
    return getExtractedMetaTitlePure(this.extractedMeta(), index);
  }

  /**
   * Toggles the 3D flip state between Front (Response) and Back (Request Payload)
   * @param {Event} event - Optional mouse event to stop propagation.
   */
  public toggleFlip(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isFlipped.update((v) => !v);
  }

  /**
   * PURE FUNCTION: Dispatches the global NGRX action to trigger the Rapider Dev Agent.
   * This is called from the UI when an endpoint action fails and the user clicks the recovery button.
   * @returns {void}
   */
  public runDevAgent(): void {
    this.store.dispatch(DevAgentActions.triggerAgent());
  }

  /**
   * Dispatches the execution response data to the main Workspace Canvas natively.
   */
  public onShowInCanvas(): void {
    const responseData = this.action.executionState?.response;
    if (!responseData) return;

    // Safely resolve the model name
    const returnType =
      this.action.payload.http?.returnType ||
      (this.action.payload.http as any)?.projectModelName ||
      this.entityName ||
      'Unknown';

    // Normalize payload to array to extract tracking IDs securely
    const dataArray = Array.isArray(responseData)
      ? responseData
      : [responseData];
    const recordIds = dataArray.map((r: any) => r.id || r._id).filter(Boolean);

    const insightPayload = {
      id: generateTempId(),
      timestamp: new Date(),
      operationType: 'READ',
      title: returnType,
      description: this.action.description,
      projectModelName: returnType,
      recordIds: recordIds,
      isSuccess: true,
      payloadData: null,
    };

    this.store.dispatch(
      ProjectDataActions.setActiveInsight({ insight: insightPayload }),
    );
  }
}
